// pages/Home.js — mobile responsive

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CtaBand from '../components/CtaBand';

export default function Home() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const review = {
    quote: "From the first visit to the final fit — honestly couldn't fault them. The team were brilliant from start to finish.",
    author: "Sarah M, Finaghy",
  };

  return (
    <div className="page-wrapper">
      <style>{`
        .hero-title {
          font-family: 'Playfair Display', serif;
          letter-spacing: -0.025em;
          margin-bottom: 1.5rem;
          line-height: 1.1;
          font-size: 2.25rem;
        }
        @media (min-width: 640px) {
          .hero-title { font-size: 3rem; }
        }
        @media (min-width: 768px) {
          .hero-title { font-size: 3.75rem; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 4.5rem; }
        }
        .hero-white {
          font-weight: 400;
          display: block;
        }
        .hero-orange {
          font-weight: 500;
          color: #F58220;
          display: block;
        }
        .hero-btns {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .strip-grid {
          display: grid;
          grid-template-columns: 1fr 3fr;
        }
        @media (max-width: 640px) {
          .strip-grid {
            grid-template-columns: 1fr;
          }
          .strip-stat {
            border-right: none !important;
            border-bottom: 1px solid var(--border);
          }
          .hero-btns {
            flex-direction: column;
            align-items: center;
          }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; transform: translateX(-50%) translateY(0); text-shadow: none; }
          50%       { opacity: 1;   transform: translateX(-50%) translateY(6px); text-shadow: 0 0 16px rgba(232,130,26,0.9); }
        }
        .hero-scroll {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          color: var(--orange);
          font-size: 1.4rem;
          animation: pulse-glow 2s ease-in-out infinite;
          cursor: pointer;
          z-index: 1;
        }
        .featured-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin-top: 3rem;
        }
        @media (max-width: 768px) {
          .featured-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
      `}</style>

      {/* ── HERO ── */}
      <section style={s.hero}>
        <div style={s.heroBg} />
        <div style={s.heroGlow} />
        <div style={s.heroContent}>
          <p style={s.heroEye}>Bespoke Kitchen Design</p>
          <h1 className="hero-title">
            <span className="hero-white">Kitchens Designed</span>
            <span className="hero-orange">Beyond the Ordinary</span>
          </h1>
          <p style={s.heroSub}>
            Crafted in Belfast. Built for Life. We create exceptional kitchen spaces
            that reflect your unique style and elevate your everyday.
          </p>
          <div className="hero-btns">
            <button className="btn-primary" onClick={() => navigate('/contact')}>Book a Consultation</button>
            <button className="btn-outline" onClick={() => navigate('/gallery')}>View Portfolio</button>
          </div>
        </div>
        <div className="hero-scroll">∨</div>
      </section>

      {/* ── STATS + REVIEW STRIP ── */}
      <div style={s.strip}>
        <div className="container">
          <div className="strip-grid">

            {/* Left — stat */}
            <div className="strip-stat" style={s.stripStat}>
              <div style={s.stripNum}>25+</div>
              <div style={s.stripLabel}>Years of Experience</div>
            </div>

            {/* Right — review */}
            <div style={s.stripQuote}>
              <div style={s.quoteText}>"{review.quote}"</div>
              <div style={s.quoteAuthor}>— {review.author}</div>
            </div>

          </div>
        </div>
      </div>

      {/* ── INTRO SECTION ── */}
      <section style={s.introSection}>
        <div className="container">
          <style>{`
            .intro-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6rem;
              align-items: center;
            }
            .intro-heading {
              font-family: 'Cormorant Garamond', serif;
              font-size: clamp(2rem, 4vw, 4rem);
              font-weight: 400;
              line-height: 1.1;
              margin-bottom: 1.5rem;
            }
            @media (max-width: 768px) {
              .intro-grid {
                grid-template-columns: 1fr;
                gap: 2.5rem;
              }
              .intro-heading {
                font-size: 2.2rem;
              }
            }
          `}</style>
          <div className="intro-grid">
            <div>
              <div className="label">Est. Belfast</div>
              <h2 className="intro-heading">Where Design<br />Meets Precision</h2>
              <p className="body-p">
                Urban Kitchens is Belfast's premier destination for bespoke kitchen design.
                We blend European craftsmanship with Northern Irish authenticity to create
                spaces that are both functional and breathtaking.
              </p>
              <p className="body-p">
                Every project begins with understanding your vision and ends with a kitchen
                that exceeds your expectations. Our meticulous attention to detail ensures
                that every element is considered, from the initial concept to the final installation.
              </p>
              <button style={s.linkArrow} onClick={() => navigate('/about')}>
                Learn Our Story →
              </button>
            </div>
            <div>
              <img src="/images/carpenter-measuring.jpg" alt="Urban Kitchens" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED PROJECTS ── */}
      <section style={s.featuredSection}>
        <div className="container">
          <div style={s.featuredHeader}>
            <div>
              <div className="label">Our Work</div>
              <h2 className="heading-xl">Featured Projects</h2>
            </div>
            <button style={s.viewAllBtn} onClick={() => navigate('/gallery')}>
              View All Projects →
            </button>
          </div>

          <div className="featured-grid">
            {[
              { title: 'Downpatrick Project', location: 'Co. Down',  tag: 'Luxury',       image: '/images/downpatrick1.jpg' },
              { title: 'Darraghcross',        location: 'Co. Down',  tag: 'Contemporary', image: '/images/Darraghcross1.jpg' },
              { title: 'Lisburn Project',     location: 'Lisburn',   tag: 'Modern',       image: '/images/lisburn-project1.jpg' },
            ].map((project, i) => (
              <div key={i} style={s.featuredCard} onClick={() => navigate('/gallery')}>
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <div style={s.featuredPlaceholder}>ADD PHOTO {i + 1}</div>
                )}
                <div style={s.featuredOverlay}>
                  <div style={s.featuredTag}>{project.tag}</div>
                  <div style={s.featuredTitle}>{project.title}</div>
                  <div style={s.featuredLoc}>{project.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to Start Your Journey?"
        text="Let's discuss your vision and explore how we can create your perfect kitchen."
        buttons={[
          { label: 'Book a Consultation', to: '/contact', variant: 'primary' },
          { label: 'View Our Work',       to: '/gallery', variant: 'outline' },
        ]}
      />
    </div>
  );
}

const s = {
  hero: {
    height: '100vh', minHeight: 600,
    position: 'relative',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center', overflow: 'hidden', padding: '0 1.5rem',
  },
  heroBg: {
    position: 'absolute', inset: 0,
    backgroundImage: `url(/images/${['IMG_5889.jpg', 'IMG_58899.jpg'][Math.floor(Math.random() * 2)]})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  
  heroGlow: {
  position: 'absolute', inset: 0,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.85) 100%)',
},

  heroContent: { position: 'relative', zIndex: 1, maxWidth: 720, paddingLeft: 0 },
  heroEye: { fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '1.5rem' },
  heroSub: { color: 'var(--text-muted)', fontSize: '0.93rem', maxWidth: 480, margin: '0 0 2.5rem' },

  // Strip
  strip: { background: 'var(--bg2)', borderBottom: '1px solid var(--border)' },
  stripStat: {
    padding: '2.5rem 2rem',
    borderRight: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-start', justifyContent: 'center',
  },
  stripNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: '2.8rem', color: 'var(--orange)', lineHeight: 1 },
  stripLabel: { fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: '0.4rem' },
  stripQuote: {
    padding: '2.5rem 2.5rem',
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
  },
  quoteText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.5rem',
    fontStyle: 'italic',
    color: 'var(--orange)',
    lineHeight: 1.55,
  },
  quoteAuthor: {
    fontSize: '0.65rem', fontWeight: 600,
    letterSpacing: '0.2em', textTransform: 'uppercase',
    color: 'var(--text-muted)', marginTop: '0.85rem',
  },

  // Intro
  introSection: { padding: '100px 0', background: 'var(--bg)' },
  linkArrow: { background: 'none', border: 'none', borderBottom: '1px solid var(--orange)', color: 'var(--orange)', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '2rem', padding: '0 0 0.2rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' },

  // Featured projects
  featuredSection: { padding: '100px 0', background: 'var(--bg2)' },
  featuredHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' },
  viewAllBtn: { background: 'none', border: 'none', borderBottom: '1px solid var(--orange)', color: 'var(--orange)', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', padding: '0 0 0.2rem', whiteSpace: 'nowrap' },
  featuredCard: { position: 'relative', overflow: 'hidden', cursor: 'pointer' },
  featuredPlaceholder: { aspectRatio: '3/4', background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: '0.75rem', letterSpacing: '0.1em', border: '1px dashed var(--border)' },
  featuredOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', padding: '2rem 1.5rem 1.5rem' },
  featuredTag: { fontSize: '0.65rem', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.3rem' },
  featuredTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem', fontWeight: 500, color: '#fff' },
  featuredLoc: { fontSize: '0.78rem', color: 'var(--text-muted)' },
};